import { observable, action, computed, runInAction } from 'mobx'
import { ISaleProgram } from './sale-program.model'
import { saleProgramService } from '../services/sale-program.service'
import { Agent } from '../libs/agent'

/**
 * Store để quản lý sale programs
 * Load một lần khi khởi tạo, giống như ProductStore
 */
export class SaleProgramStore {
  @observable allPrograms: ISaleProgram[] = []
  @observable loading = false
  @observable error: string | null = null

  private agent: Agent
  private rootStore: any

  constructor(agent: Agent, rootStore?: any) {
    this.agent = agent
    this.rootStore = rootStore
  }

  private outletId?: string

  /**
   * Set outlet và tự động reload programs
   */
  @action setOutlet(outletId: string) {
    if (this.outletId !== outletId) {
      this.outletId = outletId
      this.loadAllPrograms()
    }
  }

  /**
   * Load tất cả programs cần thiết (gọi một lần khi load trang)
   */
  async loadAllPrograms() {
    console.log('🚀 Loading sale programs...')
    
    runInAction(() => {
      this.loading = true
      this.error = null
    })

    try {
      const service = saleProgramService(this.agent)

      // Load tất cả programs với type "Giá sỉ cho nhóm sản phẩm"
      const programs = await service.getActivePrograms({
        type: 'Giá sỉ cho nhóm sản phẩm'
      })
      console.log('🚀 ~ SaleProgramStore ~ loadAllPrograms ~ programs:', programs)

      runInAction(() => {
        this.allPrograms = programs
      })
    } catch (err) {
      console.error('Error loading sale programs:', err)
      runInAction(() => {
        this.error = 'Không thể tải chương trình khuyến mãi'
        this.allPrograms = []
      })
    } finally {
      runInAction(() => {
        this.loading = false
      })
    }
  }

  /**
   * Lấy matched sale programs dựa trên outlet, customer, target
   * Giống logic trong ERP calculateDiscountBySaleProgram
   */
  getMatchedPrograms(outlet?: string, customer?: any): ISaleProgram[] {
    return this.allPrograms.filter(saleProgram => {
      // Check outlet nếu có
      if (outlet && saleProgram.outlets && saleProgram.outlets.length > 0) {
        const hasOutlet = saleProgram.outlets.some((o: any) => {
          const outletId = typeof o === 'string' ? o : o._id || o.code
          return outletId === outlet
        })
        if (!hasOutlet) return false
      }

      // Check target
      if (saleProgram.target === 'all') {
        // Nếu là "Giá sỉ cho bánh trung thu" và customer có numRemainMooncakeDiscount thì skip
        if (
          saleProgram.title && 
          saleProgram.title.match(/Giá sỉ cho bánh trung thu/i) &&
          customer && customer.numRemainMooncakeDiscount
        ) {
          return false
        }
        return true
      }

      // Check customer targets
      if (saleProgram.target && saleProgram.target.match(/customer/i) && !customer) {
        return false
      }

      return true
    })
  }

  /**
   * Tìm programs phù hợp cho một SKU
   */
  findProgramsForSku(sku: string, outlet?: string, customer?: any): ISaleProgram[] {
    const matchedPrograms = this.getMatchedPrograms(outlet, customer)
    return matchedPrograms.filter(program => {
      return program.products.some(p => {
        const productSku = p.sku || (p.product && p.product.sku)
        return productSku === sku
      })
    })
  }

  /**
   * Kiểm tra SKU có trong program nào không
   */
  isSkuInAnyProgram(sku: string, outlet?: string, customer?: any): boolean {
    return this.findProgramsForSku(sku, outlet, customer).length > 0
  }

  /**
   * Clear all programs (khi logout hoặc đổi outlet)
   */
  @action clear() {
    this.allPrograms = []
    this.error = null
    this.loading = false
  }

  /**
   * Init giống như ProductStore - load khi app khởi động
   */
  async init() {
    console.log('🚀 SaleProgramStore init called')
    
    // Kiểm tra có outlet từ currentShift hoặc currentOutlet
    let outletId = null
    
    if (this.rootStore && this.rootStore.currentShiftStore) {
      const currentShift = this.rootStore.currentShiftStore.currentShift
      const currentOutlet = this.rootStore.currentShiftStore.currentOutlet
      
      if (currentShift && currentShift.outlet) {
        outletId = typeof currentShift.outlet === 'string' ? currentShift.outlet : currentShift.outlet._id
      } else if (currentOutlet) {
        outletId = currentOutlet
      }
      
      console.log('🚀 Found outlet:', outletId)
    }

    // Load programs với hoặc không có outlet
    if (outletId) {
      this.setOutlet(outletId)
    } else {
      // Load programs không cần outlet
      this.loadAllPrograms().catch(err => {
        console.log('Sale programs will be loaded after login:', err)
      })
    }
  }
}
